package presentation.screens.allVideos

import androidx.lifecycle.SavedStateHandle
import androidx.lifecycle.viewModelScope
import androidx.navigation.toRoute
import com.kinoshazam.Navigator
import com.kinoshazam.model.Route
import data.VideoRepository
import kotlinx.coroutines.CoroutineExceptionHandler
import kotlinx.coroutines.launch
import models.VideoDetails
import org.koin.core.component.KoinComponent
import presentation.screens.allVideos.AllVideosState.ScreenType
import utils.BaseViewModel

class AllVideosViewModel(
    private val repository: VideoRepository,
    private val savedStateHandle: SavedStateHandle,
    private val navigator: Navigator,
) : BaseViewModel<AllVideosState, AllVideosMVIAction>(), KoinComponent {

    val screenType = savedStateHandle.toRoute<Route.AllVideo>().screenType
    val title = savedStateHandle.toRoute<Route.AllVideo>().title
    val genre = savedStateHandle.toRoute<Route.AllVideo>().genre
    val personId = savedStateHandle.toRoute<Route.AllVideo>().personId

    private val coroutineExceptionHandler = CoroutineExceptionHandler { _, error ->
        updateState { copy(isLoading = false, isError = true) }
    }

    private var currentPage: Int = 1

    override fun initState(): AllVideosState = AllVideosState(
        title = title,
        screenType = ScreenType.valueOf(screenType),
    )

    init {
        loadVideo()
    }

    override fun onAction(action: AllVideosMVIAction) {
        when (action) {
            is AllVideosMVIAction.OnReload -> loadVideo()
            is AllVideosMVIAction.FetchNextPage -> fetchNextPage()
            is AllVideosMVIAction.OpenDetails -> navigator.navigateTo(
                Route.Dialog.Details(
                    action.id,
                    action.isTvShow
                )
            )

            is AllVideosMVIAction.BackPressed -> navigator.back()
            is AllVideosMVIAction.SearchClicked -> navigator.navigateTo(Route.Dialog.Search)
        }
    }

    private fun loadVideo() {
        viewModelScope.launch(coroutineExceptionHandler) {
            updateState { copy(isLoading = true, isError = false) }
            val videoList = loadVideoByPage(currentPage)

            updateState {
                copy(
                    isLoading = false,
                    isError = false,
                    isNextLoading = false,
                    videos = videoList
                )
            }
        }
    }

    private fun fetchNextPage() {
        if (state.value.screenType == ScreenType.ByActor) return
        viewModelScope.launch(coroutineExceptionHandler) {
            updateState { copy(isNextLoading = true, isError = false) }
            val nextVide = loadVideoByPage(++currentPage)

            updateState {
                copy(
                    isNextLoading = false,
                    isError = false,
                    videos = nextVide?.let {
                        videos?.plus(nextVide)
                    }
                )
            }
        }
    }

    private suspend fun loadVideoByPage(page: Int): List<VideoDetails>? {
        return when (state.value.screenType) {
            ScreenType.NewMovies -> repository.getNewMovies(
                page = page,
                genre = genre
            )

            ScreenType.NewTvShows -> repository.getNewTvShows(
                page = page,
                genre = genre
            )

            ScreenType.ReleaseSoon -> repository.getReleaseSoonMovies(page)
            ScreenType.Cartoons -> repository.getCartoonsTV(page)
            ScreenType.ByActor -> {
                if (page > 1) emptyList()
                else repository.getPersonCredits(personId)
            }
        }
    }
}
